import React, { useState } from 'react';
import {
  downloadFilteredFetchRawCsv,
  getFilteredFetchRawCounts,
  type FetchRawQuery,
  type FetchRawVitalFilter,
} from '../api/database';

type Props = {
  userEmail?: string;
  onLogout: () => void;
};

type EditableFilter = {
  id: number;
  metric: FetchRawVitalFilter['metric'];
  operator: FetchRawVitalFilter['operator'];
  value: string;
};

const METRICS: Array<{ value: EditableFilter['metric']; label: string }> = [
  { value: 'heart_rate_bpm', label: 'Heart rate (bpm)' },
  { value: 'hrv', label: 'HRV (ms)' },
  { value: 'respiratory_rate', label: 'Respiratory rate (breaths/min)' },
  { value: 'spo2', label: 'SpO₂ (%)' },
  { value: 'systolic_blood_pressure', label: 'Systolic blood pressure (mmHg)' },
  { value: 'diastolic_blood_pressure', label: 'Diastolic blood pressure (mmHg)' },
  { value: 'temperature', label: 'Temperature (°C)' },
  { value: 'activity', label: 'Activity' },
  { value: 'activ_intensity', label: 'Activity intensity' },
];

const TEXT_ONLY_METRICS = new Set<EditableFilter['metric']>(['activity', 'activ_intensity']);

const getOperatorsForMetric = (metric: EditableFilter['metric']): FetchRawVitalFilter['operator'][] => (
  TEXT_ONLY_METRICS.has(metric) ? ['=', '!='] : ['>', '>=', '<', '<=', '=']
);

const initialFilter = (): EditableFilter => ({
  id: Date.now() + Math.random(),
  metric: 'heart_rate_bpm',
  operator: '<',
  value: '',
});

/** Local wall-clock for datetime-local inputs: YYYY-MM-DDTHH:mm */
const getLocalDateTimeValue = (date = new Date()): string => {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return localDate.toISOString().slice(0, 16);
};

const datetimeLocalToIso = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString();
};

const datePartFromDateTimeLocal = (value: string): string => {
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
  return match ? match[1] : '';
};

type StartOperator = '>' | '>=';
type EndOperator = '<' | '<=';

const AdminFetchRawPage: React.FC<Props> = ({ userEmail, onLogout }) => {
  const nowLocal = getLocalDateTimeValue();
  const [start, setStart] = useState('');
  const [end, setEnd] = useState(() => getLocalDateTimeValue());
  const [patId, setPatId] = useState('');
  const [startOperator, setStartOperator] = useState<StartOperator>('>=');
  const [endOperator, setEndOperator] = useState<EndOperator>('<=');
  const [filters, setFilters] = useState<EditableFilter[]>([initialFilter()]);
  const [combine, setCombine] = useState<'and' | 'or'>('and');
  const [results, setResults] = useState<{ vitals: number; raw: number; accel: number; hz32: number } | null>(null);
  const [fetching, setFetching] = useState(false);
  const [downloading, setDownloading] = useState<'vitals' | 'raw' | 'accel' | '32hz' | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const resetResults = () => {
    setResults(null);
    setMessage(null);
  };

  const updateFilter = (id: number, updates: Partial<EditableFilter>) => {
    resetResults();
    setFilters((current) => current.map((filter) => {
      if (filter.id !== id) return filter;
      const nextFilter = { ...filter, ...updates };
      if (updates.metric) {
        const allowedOperators = getOperatorsForMetric(updates.metric);
        if (!allowedOperators.includes(nextFilter.operator)) {
          nextFilter.operator = allowedOperators[0];
        }
      }
      return nextFilter;
    }));
  };

  const buildQuery = (): FetchRawQuery | null => {
    if (!start || !end) {
      setError('Choose both a From and To date and time.');
      return null;
    }
    const startIso = datetimeLocalToIso(start);
    const endIso = datetimeLocalToIso(end);
    if (!startIso || !endIso || new Date(endIso).getTime() < new Date(startIso).getTime()) {
      setError('The To timestamp must be on or after the From timestamp.');
      return null;
    }

    const fromDate = datePartFromDateTimeLocal(start);
    const toDate = datePartFromDateTimeLocal(end);
    if (!fromDate || !toDate) {
      setError('Choose valid From and To date and time values.');
      return null;
    }

    const completedFilters: FetchRawVitalFilter[] = [];
    for (const filter of filters) {
      const trimmedValue = filter.value.trim();
      if (!trimmedValue) continue;
      const allowedOperators = getOperatorsForMetric(filter.metric);
      if (!allowedOperators.includes(filter.operator)) {
        setError(`The ${filter.metric} filter only supports ${allowedOperators.join(' or ')}.`);
        return null;
      }
      if (TEXT_ONLY_METRICS.has(filter.metric)) {
        completedFilters.push({ metric: filter.metric, operator: filter.operator, value: trimmedValue });
        continue;
      }
      const numericValue = Number(trimmedValue);
      if (!Number.isFinite(numericValue)) {
        setError('Numeric filters require a numeric value.');
        return null;
      }
      completedFilters.push({ metric: filter.metric, operator: filter.operator, value: numericValue });
    }
    const trimmedPatId = patId.trim();
    if (trimmedPatId && !/^\d+$/.test(trimmedPatId)) {
      setError('Patient ID must be a number when provided.');
      return null;
    }
    return {
      start: startIso,
      end: endIso,
      startOperator,
      endOperator,
      fromDate,
      toDate,
      tzOffsetHours: -new Date().getTimezoneOffset() / 60,
      patId: trimmedPatId || undefined,
      filters: completedFilters,
      combine,
    };
  };

  const fetchResults = async () => {
    setError(null);
    setMessage(null);
    const query = buildQuery();
    if (!query) return;

    setFetching(true);
    try {
      const counts = await getFilteredFetchRawCounts(query);
      const nextResults = {
        vitals: counts.vitals_count,
        raw: counts.raw_count,
        accel: counts.accel_count ?? 0,
        hz32: counts.ppg_accel_count ?? counts.sleep_count ?? 0,
      };
      setResults(nextResults);
      const minuteSlots = counts.minute_slots_in_range;
      const missingMinutes = counts.missing_minute_slots;
      const minuteSummary = minuteSlots != null
        ? ` covering ${counts.distinct_minute_count ?? nextResults.vitals}/${minuteSlots} minute slot${minuteSlots === 1 ? '' : 's'}`
          + (missingMinutes ? ` (${missingMinutes} missing)` : '')
        : '';
      setMessage(
        `Fetched ${nextResults.vitals} vital record${nextResults.vitals === 1 ? '' : 's'}${minuteSummary}, `
        + `${nextResults.raw} raw ECG/PPG sample${nextResults.raw === 1 ? '' : 's'}, `
        + `${nextResults.accel} accelerometer sample${nextResults.accel === 1 ? '' : 's'}, `
        + `and ${nextResults.hz32} PPG+accel minute window${nextResults.hz32 === 1 ? '' : 's'}.`
      );
    } catch (fetchError) {
      setError(fetchError instanceof Error ? fetchError.message : 'The filtered data could not be fetched.');
    } finally {
      setFetching(false);
    }
  };

  const download = async (exportType: 'vitals' | 'raw' | 'accel' | '32hz') => {
    const query = buildQuery();
    if (!query) return;

    setError(null);
    setMessage(null);
    setDownloading(exportType);
    try {
      await downloadFilteredFetchRawCsv(exportType, query);
      const label = exportType === 'vitals'
        ? 'Vitals'
        : exportType === 'accel'
          ? 'Accelerometer'
          : exportType === '32hz'
            ? 'PPG + accel raw'
          : 'Raw ECG/PPG';
      setMessage(`${label} CSV download started.`);
    } catch (downloadError) {
      setError(downloadError instanceof Error ? downloadError.message : 'The CSV could not be downloaded.');
    } finally {
      setDownloading(null);
    }
  };

  const handleStartChange = (nextStart: string) => {
    if (end && nextStart > end) {
      setError('The From timestamp cannot be later than the To timestamp.');
      return;
    }
    resetResults();
    setError(null);
    setStart(nextStart);
  };

  const handleEndChange = (nextEnd: string) => {
    if (start && nextEnd < start) {
      setError('The To timestamp cannot be earlier than the From timestamp.');
      return;
    }
    resetResults();
    setError(null);
    setEnd(nextEnd);
  };

  return (
    <div className="admin-errors-page fetchraw-page">
      <header className="admin-errors-header">
        <div>
          <h1>Filtered Raw Data Export</h1>
          <p className="admin-errors-subtitle">
            Export vital-sign snapshots and their matching ECG/PPG and accelerometer samples{userEmail ? ` · ${userEmail}` : ''}
          </p>
        </div>
        <button type="button" className="btn btn-login admin-errors-logout" onClick={onLogout}>
          Log out
        </button>
      </header>

      <main className="fetchraw-card">
        <div className="fetchraw-date-grid">
          <label>
            From timestamp
            <div className="fetchraw-timestamp-row">
              <select
                value={startOperator}
                onChange={(event) => {
                  resetResults();
                  setStartOperator(event.target.value as StartOperator);
                }}
                aria-label="From timestamp comparison"
              >
                <option value=">">&gt; after</option>
                <option value=">=">≥ on or after</option>
              </select>
              <input
                type="datetime-local"
                value={start}
                max={end || nowLocal}
                onChange={(event) => handleStartChange(event.target.value)}
                aria-label="From date and time"
              />
            </div>
          </label>
          <label>
            To timestamp
            <div className="fetchraw-timestamp-row">
              <select
                value={endOperator}
                onChange={(event) => {
                  resetResults();
                  setEndOperator(event.target.value as EndOperator);
                }}
                aria-label="To timestamp comparison"
              >
                <option value="<">&lt; before</option>
                <option value="<=">≤ on or before</option>
              </select>
              <input
                type="datetime-local"
                value={end}
                min={start || undefined}
                max={nowLocal}
                onChange={(event) => handleEndChange(event.target.value)}
                aria-label="To date and time"
              />
            </div>
          </label>
        </div>

        <label>
          Patient ID (optional)
          <input
            type="text"
            inputMode="numeric"
            value={patId}
            onChange={(event) => {
              resetResults();
              setPatId(event.target.value);
            }}
            placeholder="e.g. 42"
            aria-label="Patient ID filter"
          />
        </label>

        <div className="fetchraw-filter-heading">
          <div>
            <h2>Vital filters</h2>
            <p>Leave a value blank to ignore that row.</p>
          </div>
          <label className="fetchraw-combine">
            Match
            <select
              value={combine}
              onChange={(event) => {
                resetResults();
                setCombine(event.target.value as 'and' | 'or');
              }}
            >
              <option value="and">all filters</option>
              <option value="or">any filter</option>
            </select>
          </label>
        </div>

        <div className="fetchraw-filter-list">
          {filters.map((filter) => (
            <div className="fetchraw-filter-row" key={filter.id}>
              <select value={filter.metric} onChange={(event) => updateFilter(filter.id, { metric: event.target.value as EditableFilter['metric'] })}>
                {METRICS.map((metric) => <option key={metric.value} value={metric.value}>{metric.label}</option>)}
              </select>
              <select value={filter.operator} onChange={(event) => updateFilter(filter.id, { operator: event.target.value as EditableFilter['operator'] })}>
                {getOperatorsForMetric(filter.metric).map((operator) => (
                  <option key={operator} value={operator}>
                    {operator === '>' ? '>' : operator === '>=' ? '≥' : operator === '<' ? '<' : operator === '<=' ? '≤' : operator}
                  </option>
                ))}
              </select>
              <input
                type={TEXT_ONLY_METRICS.has(filter.metric) ? 'text' : 'number'}
                inputMode={TEXT_ONLY_METRICS.has(filter.metric) ? 'text' : 'decimal'}
                value={filter.value}
                onChange={(event) => updateFilter(filter.id, { value: event.target.value })}
                placeholder={TEXT_ONLY_METRICS.has(filter.metric) ? 'Exact text value' : 'Value'}
                aria-label="Filter value"
              />
              <button
                type="button"
                className="btn admin-errors-clear"
                onClick={() => {
                  resetResults();
                  setFilters((current) => current.length === 1 ? [initialFilter()] : current.filter((item) => item.id !== filter.id));
                }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          className="btn admin-errors-refresh"
          onClick={() => {
            resetResults();
            setFilters((current) => [...current, initialFilter()]);
          }}
        >
          Add vital filter
        </button>

        {error && <div className="admin-errors-alert">{error}</div>}
        {message && <div className="fetchraw-success">{message}</div>}

        <div className="fetchraw-actions">
          <button type="button" className="btn admin-errors-refresh" disabled={fetching || downloading !== null} onClick={fetchResults}>
            {fetching ? 'Fetching…' : 'Fetch'}
          </button>
          <button type="button" className="btn btn-login" disabled={fetching || downloading !== null || !results || results.vitals === 0} onClick={() => download('vitals')}>
            {downloading === 'vitals' ? 'Preparing…' : 'Download vitals CSV'}
          </button>
          <button type="button" className="btn btn-login" disabled={fetching || downloading !== null || !results || results.raw === 0} onClick={() => download('raw')}>
            {downloading === 'raw' ? 'Preparing…' : 'Download raw ECG/PPG CSV'}
          </button>
          <button type="button" className="btn btn-login" disabled={fetching || downloading !== null || !results || results.accel === 0} onClick={() => download('accel')}>
            {downloading === 'accel' ? 'Preparing…' : 'Download accelerometer CSV'}
          </button>
          <button type="button" className="btn btn-login" disabled={fetching || downloading !== null || !results || results.hz32 === 0} onClick={() => download('32hz')}>
            {downloading === '32hz' ? 'Preparing…' : 'Download PPG + accel raw'}
          </button>
        </div>
      </main>
    </div>
  );
};

export default AdminFetchRawPage;
